import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { LightDebtorSchema } from 'shared/hooks/use-load-light-debtors';
import { z } from 'zod/v4';
import { AttachedInvoicesSchema } from './schemas/attached-invoices';
import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const MessageSchema = z.object({
  id: z.coerce.string(),
  isSystem: z.boolean(),
  body: z.string(),
  author: z.string().nullable(),
  owner: z.boolean(),
  attachmentUrl: z.string().nullable(),
  attachmentName: z.string().nullable(),
  date: z.string(),
});

const ConversationSchema = z.object({
  id: z.coerce.string(),
  unread: z.boolean(),
  messages: z.array(MessageSchema),
});

const InvoiceFeedbackSchema = z
  .object({
    id: z.coerce.number(),
    hasTpCasePDF: z.boolean().nullable(),
    closed: z.boolean(),
    thirdPartyCaseId: z.number().nullable(),
    conversation: ConversationSchema,
  })
  .nullable();

const ThirdPartyCaseSchema = z.object({
  id: z.coerce.number(),
  caseType: z.string(),
  started: z.boolean(),
  closed: z.boolean(),
  followedUp: z.boolean(),
  caseId: z.string(),
  invoiceReference: z.string(),
  invoiceId: z.number(),
  tpOwner: z.string().optional(),
  createdAt: z.string(),
  lastUpdate: z.string().nullable(),
  totalTvac: z.string(),
  totalRemaining: z.string(),
  debtor: LightDebtorSchema,
  invoiceFeedback: InvoiceFeedbackSchema,
  attachedInvoices: AttachedInvoicesSchema,
});

export type ThirdPartyCase = z.infer<typeof ThirdPartyCaseSchema>;
export type InvoiceFeedback = z.infer<typeof InvoiceFeedbackSchema>;

export async function loadThirdPartyCaseFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get(`/thirdparty_cases/${queryKey[1]}`);

  return ThirdPartyCaseSchema.parse(data);
}

export function useLoadThirdPartyCase(externalMailId: number) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<ThirdPartyCase, ApiError>({
    queryKey: ['third-party-case', externalMailId],
    queryFn: context => loadThirdPartyCaseFn(axiosInstance, context),
  });

  return addResourceNameToQueryResult<ThirdPartyCase, ApiError, 'thirdPartyCase'>(
    'thirdPartyCase',
    queryResult,
  );
}
